import { useState, useEffect, useRef } from "react";
import { supabase } from "./supabase";
import { getHatchRecommendations, getRegion, detectSpotType } from "./App";
import "./FishWhistler.css";

const EDGE_FN_URL = "https://lpdmfvvnqvubdataqaag.supabase.co/functions/v1/fish-whistler";

const TIER_LIMITS = { free: 3, angler: 20, guide: 999 };

const TIER_DEPTH = {
  free: `Give a focused, useful answer in 3-4 sentences. Be specific — name an actual fly, lure, or spot. 
No filler, no hedging. Give them one thing they can act on right now. 
End with natural energy, like you genuinely want them to go catch a fish. 
Leave them feeling like they got real intel, but naturally curious for more.`,

  angler: `Give a thorough, confident answer — conditions, tactics, the right gear with specific sizes and colors, and timing. 
Cover the what, the where, and the why. Think "great guide on the phone" — conversational, packed with real value, nothing wasted. 
If there's a nuance worth knowing (water temp, time of day, approach angle, tippet size), include it. 
This person is serious about fishing — treat them that way. Pure prose, no lists or headers.`,

  guide: `Give a genuinely expert answer. Cover everything that matters: specific fly or lure recommendations with sizes and colors, 
water conditions and how to read them, exact timing windows, approach and presentation details, any access or regulation notes worth knowing, 
and at least one thing they probably haven't considered — a pro tip, a lesser-known access point, a pattern that outperforms the obvious choice. 
This person paid for the full picture — deliver it. Pure conversational prose only, no bullets or headers. 
Sound like the best guide they've ever had who also happens to be a great time on the water. 
If the conditions or season suggest something specific, call it out directly and confidently.`,
};

const LOGO_MINI = "/FishWhistlerMini.png";
const LOGO_MAIN = "/Fish_Whistler_logo1_transparent.png";

// ─────────────────────────────────────────────
// ZIP → COORDS
// ─────────────────────────────────────────────
async function zipToCoords(zip) {
  try {
    const r = await fetch(`https://api.zippopotam.us/us/${zip}`);
    if (!r.ok) return null;
    const d = await r.json();
    const p = d?.places?.[0];
    if (p) return { lat: parseFloat(p.latitude), lng: parseFloat(p.longitude) };
  } catch {}
  return null;
}

// ─────────────────────────────────────────────
// WEATHER FORECAST (5-day via Open-Meteo)
// ─────────────────────────────────────────────
async function fetchWeatherForecast(lat, lng) {
  try {
    const url = new URL("https://api.open-meteo.com/v1/forecast");
    url.searchParams.set("latitude",          lat);
    url.searchParams.set("longitude",         lng);
    url.searchParams.set("current",           "temperature_2m,weather_code,wind_speed_10m,surface_pressure,relative_humidity_2m");
    url.searchParams.set("daily",             "weather_code,temperature_2m_max,temperature_2m_min,precipitation_sum,wind_speed_10m_max,sunrise,sunset");
    url.searchParams.set("temperature_unit",  "fahrenheit");
    url.searchParams.set("wind_speed_unit",   "mph");
    url.searchParams.set("precipitation_unit","inch");
    url.searchParams.set("forecast_days",     "5");
    url.searchParams.set("timezone",          "auto");

    const r = await fetch(url.toString(), { signal: AbortSignal.timeout(8000) });
    if (!r.ok) return null;
    const d = await r.json();

    const wdesc = code => code === 0 ? "Clear" : code <= 3 ? "Partly cloudy" : code <= 49 ? "Foggy" : code <= 67 ? "Rain" : code <= 77 ? "Snow" : code <= 82 ? "Showers" : "Thunderstorm";

    return {
      current: {
        temp:     Math.round(d.current.temperature_2m),
        desc:     wdesc(d.current.weather_code),
        wind:     Math.round(d.current.wind_speed_10m),
        pressure: Math.round(d.current.surface_pressure),
        humidity: Math.round(d.current.relative_humidity_2m),
      },
      forecast: d.daily.time.slice(0, 5).map((date, i) => ({
        date,
        high:    Math.round(d.daily.temperature_2m_max[i]),
        low:     Math.round(d.daily.temperature_2m_min[i]),
        desc:    wdesc(d.daily.weather_code[i]),
        precip:  Number(d.daily.precipitation_sum[i]).toFixed(2),
        wind:    Math.round(d.daily.wind_speed_10m_max[i]),
        sunrise: (d.daily.sunrise[i] || "").slice(11) || "—",
        sunset:  (d.daily.sunset[i]  || "").slice(11) || "—",
      })),
    };
  } catch { return null; }
}
// ─────────────────────────────────────────────
// LIVE DATA FETCHERS
// ─────────────────────────────────────────────

async function fetchUSGS(gaugeId) {
  if (!gaugeId || gaugeId === "N/A") return null;
  try {
    const url = `https://waterservices.usgs.gov/nwis/iv/?sites=${gaugeId}&parameterCd=00060,00010&format=json&siteStatus=active`;
    const res  = await fetch(url);
    if (!res.ok) return null;
    const data = await res.json();
    const series = data?.value?.timeSeries ?? [];
    const result = {};
    for (const s of series) {
      const code = s.variable?.variableCode?.[0]?.value;
      const val  = parseFloat(s.values?.[0]?.value?.[0]?.value);
      if (!isNaN(val)) {
        if (code === "00060") result.cfs   = val;
        if (code === "00010") result.tempC = val;
      }
    }
    if (result.tempC != null) result.tempF = Math.round(result.tempC * 9/5 + 32);
    return Object.keys(result).length ? result : null;
  } catch { return null; }
}

async function fetchWeather(lat, lng) {
  if (!lat || !lng) return null;
  try {
    const ptRes  = await fetch(`https://api.weather.gov/points/${lat.toFixed(4)},${lng.toFixed(4)}`);
    if (!ptRes.ok) return null;
    const ptData = await ptRes.json();
    const fcastUrl = ptData?.properties?.forecast;
    if (!fcastUrl) return null;
    const fRes  = await fetch(fcastUrl);
    if (!fRes.ok) return null;
    const fData = await fRes.json();
    const periods = (fData?.properties?.periods ?? []).slice(0, 4).map(p => ({
      name:      p.name,
      temp:      p.temperature,
      tempUnit:  p.temperatureUnit,
      short:     p.shortForecast,
      detail:    p.detailedForecast,
      isDaytime: p.isDaytime,
      windSpeed: p.windSpeed,
      windDir:   p.windDirection,
      precip:    p.probabilityOfPrecipitation?.value ?? null,
    }));
    return periods.length ? periods : null;
  } catch { return null; }
}

// ─────────────────────────────────────────────
// CONTEXT BUILDER
// ─────────────────────────────────────────────

function buildLiveContext(spot, usgs, weather, hatch) {
  if (!spot) return "";

  const parts = [];

  // ── Spot identity ──
  parts.push(`CURRENT SPOT: ${spot.name} — ${spot.state}`);
  if (spot.description) parts.push(`About this water: ${spot.description}`);
  if (spot.tips)        parts.push(`Local intel: ${spot.tips}`);
  if (spot.regulations) parts.push(`Regulations: ${spot.regulations}`);
  if (spot.species?.length) parts.push(`Target species: ${spot.species.join(", ")}`);

  // ── Live flow ──
  if (usgs) {
    const flowLine = usgs.cfs != null
      ? `Current flow: ${usgs.cfs} CFS (ideal range for this spot: ${spot.flowRange?.[0]}–${spot.flowRange?.[1]} CFS)`
      : null;
    const tempLine = usgs.tempF != null ? `Water temp: ${usgs.tempF}°F` : null;
    if (flowLine || tempLine) {
      parts.push("LIVE STREAM CONDITIONS:");
      if (flowLine) parts.push(flowLine);
      if (tempLine) parts.push(tempLine);

      const [mn, mx] = spot.flowRange ?? [0, 9999];
      if (usgs.cfs != null) {
        if (usgs.cfs > mx * 1.5)      parts.push("Flow assessment: HIGH — fish the edges and eddies, avoid midchannel.");
        else if (usgs.cfs < mn * 0.5) parts.push("Flow assessment: LOW — water is clear and fish are spooky, long leaders and fine tippet.");
        else                           parts.push("Flow assessment: GOOD — within ideal range, prime conditions.");
      }
    }
  } else if (spot.usgsGauge && spot.usgsGauge !== "N/A") {
    parts.push("Live flow data unavailable for this gauge right now.");
  }

  // ── Hatch engine output — IMPROVEMENT 1: bumped to 6 flies / 4 lures ──
  if (hatch) {
    const topFlies = hatch.fly?.slice(0, 6).map(f => `${f.n} (${f.d})`).join(", ");
    const topLures = hatch.spin?.slice(0, 4).map(f => `${f.n} (${f.d})`).join(", ");
    const notes    = hatch.notes?.filter(Boolean).join(" ");
    parts.push("HATCH ENGINE — TOP PATTERNS RIGHT NOW:");
    if (topFlies) parts.push(`Fly: ${topFlies}`);
    if (topLures) parts.push(`Spin: ${topLures}`);
    if (notes)    parts.push(`Condition notes: ${notes}`);
  }

  // ── Weather ──
  if (weather?.length) {
    parts.push("UPCOMING WEATHER:");
    for (const p of weather) {
      const precipStr = p.precip != null ? `, ${p.precip}% precip` : "";
      parts.push(`${p.name}: ${p.temp}°${p.tempUnit}, ${p.short}, wind ${p.windSpeed} ${p.windDir}${precipStr}`);
    }
    const signals = [];
    const dayPeriods = weather.filter(p => p.isDaytime);
    if (dayPeriods.some(p => (p.precip ?? 0) > 60))    signals.push("Rain likely — expect rising flows 6–12 hours after heavy precip.");
    if (dayPeriods.some(p => p.temp > 90))              signals.push("Heat warning — fish will be stressed midday, target early morning and evening.");
    if (dayPeriods.some(p => p.temp < 32))              signals.push("Freezing temps — guides ice up, fish midday when it's warmest.");
    if (dayPeriods.some(p => (p.windSpeed || "").includes("mph") && parseInt(p.windSpeed) > 20))
                                                         signals.push("High winds — casting will be tough, fish downwind banks.");
    if (signals.length) parts.push("Weather signals for anglers: " + signals.join(" "));
  }

  parts.push(`Current month: ${new Date().toLocaleString("en-US", { month: "long" })}`);
  return parts.join("\n");
}

// ─────────────────────────────────────────────
// IMPROVEMENT 2: zip-based fallback hatch
// Derives a plausible region/spotType hatch when no spot is selected,
// so the no-spot briefing mode still gets hatch-informed answers.
// ─────────────────────────────────────────────

function buildFallbackHatch(zipCode, month) {
  // Rough zip-prefix → state → region mapping
  // Covers the most common zip prefixes; falls back to "east" freestone
  const zip = String(zipCode || "");
  const prefix = parseInt(zip.slice(0, 3), 10);

  let state = "VA"; // default
  if      (prefix >= 100 && prefix <= 149) state = "NY";
  else if (prefix >= 150 && prefix <= 196) state = "PA";
  else if (prefix >= 197 && prefix <= 199) state = "DE";
  else if (prefix >= 200 && prefix <= 205) state = "DC";
  else if (prefix >= 206 && prefix <= 212) state = "MD";
  else if (prefix >= 220 && prefix <= 246) state = "VA";
  else if (prefix >= 247 && prefix <= 268) state = "WV";
  else if (prefix >= 270 && prefix <= 289) state = "NC";
  else if (prefix >= 290 && prefix <= 299) state = "SC";
  else if (prefix >= 300 && prefix <= 319) state = "GA";
  else if (prefix >= 320 && prefix <= 349) state = "FL";
  else if (prefix >= 350 && prefix <= 369) state = "AL";
  else if (prefix >= 370 && prefix <= 385) state = "TN";
  else if (prefix >= 386 && prefix <= 397) state = "MS";
  else if (prefix >= 400 && prefix <= 427) state = "KY";
  else if (prefix >= 430 && prefix <= 458) state = "OH";
  else if (prefix >= 460 && prefix <= 479) state = "IN";
  else if (prefix >= 480 && prefix <= 499) state = "MI";
  else if (prefix >= 500 && prefix <= 528) state = "IA";
  else if (prefix >= 530 && prefix <= 549) state = "WI";
  else if (prefix >= 550 && prefix <= 567) state = "MN";
  else if (prefix >= 570 && prefix <= 577) state = "SD";
  else if (prefix >= 580 && prefix <= 588) state = "ND";
  else if (prefix >= 590 && prefix <= 599) state = "MT";
  else if (prefix >= 600 && prefix <= 629) state = "IL";
  else if (prefix >= 630 && prefix <= 658) state = "MO";
  else if (prefix >= 660 && prefix <= 679) state = "KS";
  else if (prefix >= 680 && prefix <= 693) state = "NE";
  else if (prefix >= 700 && prefix <= 714) state = "LA";
  else if (prefix >= 716 && prefix <= 729) state = "AR";
  else if (prefix >= 730 && prefix <= 749) state = "OK";
  else if (prefix >= 750 && prefix <= 799) state = "TX";
  else if (prefix >= 800 && prefix <= 816) state = "CO";
  else if (prefix >= 820 && prefix <= 831) state = "WY";
  else if (prefix >= 832 && prefix <= 838) state = "ID";
  else if (prefix >= 840 && prefix <= 847) state = "UT";
  else if (prefix >= 850 && prefix <= 865) state = "AZ";
  else if (prefix >= 870 && prefix <= 884) state = "NM";
  else if (prefix >= 889 && prefix <= 898) state = "NV";
  else if (prefix >= 900 && prefix <= 961) state = "CA";
  else if (prefix >= 970 && prefix <= 979) state = "OR";
  else if (prefix >= 980 && prefix <= 994) state = "WA";

  const mockSpot = {
    state,
    species: ["Brown Trout", "Rainbow Trout"],
    spotType: "freestone",
    name: "Local Water",
    description: "",
    tips: "",
  };

  return getHatchRecommendations(mockSpot, month, {});
}

// ─────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────

export default function FishWhistler({ spot = null, nav = () => {} }) {
  const [user, setUser]         = useState(null);
  const [profile, setProfile]   = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput]       = useState("");
  const [loading, setLoading]   = useState(false);
  const [briefing, setBriefing] = useState(null);
  const [briefingLoading, setBriefingLoading] = useState(false);

  const [usgsData,    setUsgsData]    = useState(null);
  const [weatherData, setWeatherData] = useState(null);
  const [hatchData,   setHatchData]   = useState(null);
  const [condLoading, setCondLoading] = useState(false);
  const [forecastData, setForecastData] = useState(null);

  const bottomRef = useRef(null);
  const lastReplyRef = useRef(null);

  // ── Auth + profile ──
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => setUser(session?.user ?? null));
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_, s) => setUser(s?.user ?? null));
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!user) return;
    supabase.from("profiles")
      .select("tier, queries_today, queries_reset_at, zip_code, email")
      .eq("id", user.id).single()
      .then(({ data }) => setProfile(data));
  }, [user]);

  useEffect(() => {
    if (lastReplyRef.current) {
      lastReplyRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, briefing]);

  // ── Fetch live conditions whenever spot changes ──
  useEffect(() => {
    if (!spot) return;
    setUsgsData(null);
    setWeatherData(null);
    setHatchData(null);
    setBriefing(null);
    setMessages([]);
    setCondLoading(true);

    const month = new Date().getMonth() + 1;
    const spotType = spot.spotType || detectSpotType(spot);
    const enrichedSpot = { ...spot, spotType };

    Promise.all([
      fetchUSGS(spot.usgsGauge),
      fetchWeather(spot.lat, spot.lng),
    ]).then(([usgs, weather]) => {
      setUsgsData(usgs);
      setWeatherData(weather);

      const conditions = {};
      if (usgs?.cfs   != null) { conditions.flowCfs = usgs.cfs; conditions.flowRangeIdeal = spot.flowRange; }
      if (usgs?.tempF != null) conditions.waterTempF = usgs.tempF;
      const hatch = getHatchRecommendations(enrichedSpot, month, conditions);
      setHatchData(hatch);
      setCondLoading(false);
    });
  }, [spot?.id ?? spot?.name]);

  // ── Fetch briefing once live data is ready ──
  useEffect(() => {
    if (!profile || !user || briefing || briefingLoading || condLoading) return;
    if (spot && (usgsData === null && weatherData === null)) return;

    const timer = setTimeout(async () => {
      // Fetch forecast from zip if we don't already have one
      if (!forecastData && profile.zip_code) {
        const coords = await zipToCoords(profile.zip_code);
        if (coords) {
          const forecast = await fetchWeatherForecast(coords.lat, coords.lng);
          setForecastData(forecast);
          fetchBriefing(profile, forecast); // pass it directly so we don't wait for state
        } else {
          fetchBriefing(profile, null);
        }
      } else {
        fetchBriefing(profile, forecastData);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [profile, user, condLoading]);

  // ─────────────────────────────────────────────
  // BRIEFING
  // ─────────────────────────────────────────────
  async function fetchBriefing(prof, forecast = null) {
  setBriefingLoading(true);
  const zip   = prof?.zip_code || "unknown";
  const today = new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
  const month = new Date().getMonth() + 1;
  const liveCtx = buildLiveContext(spot, usgsData, weatherData, hatchData);

  // Build forecast block for prompt
  const fw = forecast;
  const forecastBlock = fw ? [
    `Current: ${fw.current.temp}°F, ${fw.current.desc}, wind ${fw.current.wind} mph, pressure ${fw.current.pressure} hPa (${fw.current.pressure > 1015 ? "rising — fish likely active" : fw.current.pressure < 1005 ? "dropping — fish may go deep" : "stable"}), humidity ${fw.current.humidity}%`,
    "5-Day Forecast:",
    ...fw.forecast.map((d, i) => {
      const label = i === 0 ? "Today" : new Date(d.date + "T12:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
      const precip = parseFloat(d.precip) > 0 ? `, ${d.precip}″ precip` : "";
      return `  ${label}: ${d.desc}, H ${d.high}°F / L ${d.low}°F, wind ${d.wind} mph${precip}, sunrise ${d.sunrise} / sunset ${d.sunset}`;
    }),
  ].join("\n") : "Forecast unavailable.";

  let prompt;
  if (spot) {
    prompt = `TODAY IS ${today}.

${liveCtx}

WEATHER FORECAST:
${forecastBlock}

The user is looking at this spot in the One Last Cast app. Give them a punchy, specific daily briefing for ${spot.name}. 
No preamble, just dive in. Cover these 4 things as short natural paragraphs — no emojis, no bullet points, pure prose:

1. How are conditions right now and over the next couple days — use the live flow, water temp, and forecast above. Be specific about what it means for fishing.
2. Exactly what to tie on or rig up — use the hatch engine patterns above, name specific flies or lures with sizes and colors.
3. Target species and realistic expectations given current conditions.
4. The best time window and one tactical tip that most people would miss.

Under 180 words. Sound like the best guide they've ever had who just stepped off this exact water an hour ago. Finish with one short line that makes them want to grab their rod right now.`;

  } else {
    const fallbackHatch = buildFallbackHatch(zip, month);
    const topFlies  = fallbackHatch.fly?.slice(0, 6).map(f => `${f.n} (${f.d})`).join(", ");
    const topLures  = fallbackHatch.spin?.slice(0, 4).map(f => `${f.n} (${f.d})`).join(", ");
    const hatchNotes = fallbackHatch.notes?.filter(Boolean).join(" ");

    prompt = `TODAY IS ${today}. USER ZIP CODE: ${zip}.

WEATHER FORECAST FOR THIS AREA:
${forecastBlock}

HATCH ENGINE — REGIONAL PATTERNS (${new Date().toLocaleString("en-US", { month: "long" })}):
Fly: ${topFlies}
Spin: ${topLures}
${hatchNotes ? `Condition notes: ${hatchNotes}` : ""}

Give a punchy "Today's Fishing Briefing" for this user. No preamble — just dive straight in. 
Pure conversational prose, no bullet points, no headers, no emojis. Four short natural paragraphs:

1. Best spot near zip ${zip} today — name a real, specific location near this zip code and why it's the pick right now given the weather above. Do NOT suggest anything more than 75 miles away.
2. What to tie on — draw from the hatch engine patterns above. Name a specific fly or lure with size and color, and exactly why it works right now.
3. Target species and a realistic quality expectation.
4. Best time window today — use the sunrise/sunset from the forecast above — and one tactical tip.

Under 180 words. Sound like the greatest guide alive. End with one short punchy line that makes them want to drop everything and go.`;
  }

  try {
    const { data: { session } } = await supabase.auth.getSession();
    let token = session?.access_token;
    if (!token) {
      const { data: { session: r } } = await supabase.auth.refreshSession();
      token = r?.access_token;
    }
    if (!token) { setBriefing("Sign out and back in to load your briefing."); setBriefingLoading(false); return; }

    const res = await fetch(EDGE_FN_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
      body: JSON.stringify({ message: prompt, messages: [{ role: "user", content: prompt }], isBriefing: true }),
    });
    const json = await res.json();
    setBriefing(json.reply || "Couldn't load today's briefing — try refreshing!");
  } catch {
    setBriefing("Couldn't load today's briefing — check your connection.");
  } finally {
    setBriefingLoading(false);
  }
}

  // ─────────────────────────────────────────────
  // SEND MESSAGE
  // ─────────────────────────────────────────────
  async function sendMessage() {
    if (!input.trim() || loading) return;
    const limit = TIER_LIMITS[profile?.tier] ?? TIER_LIMITS.free;
    if ((profile?.queries_today ?? 0) >= limit) return;

    const userMsg = { role: "user", content: input.trim() };
    setMessages(prev => [...prev, userMsg]);
    setInput("");
    setLoading(true);
    const history = [...messages, userMsg];

    const tier      = profile?.tier ?? "free";
    const zip       = profile?.zip_code || "unknown";
    const month     = new Date().getMonth() + 1;
    const depthNote = TIER_DEPTH[tier] ?? TIER_DEPTH.free;

    // IMPROVEMENT 2: use fallback hatch in no-spot chat mode too
    const activeHatch = hatchData ?? (zip !== "unknown" ? buildFallbackHatch(zip, month) : null);
    const liveCtx = buildLiveContext(spot, usgsData, weatherData, activeHatch);

    const fw = forecastData;
    const forecastBlock = fw ? [
      `Current: ${fw.current.temp}°F, ${fw.current.desc}, wind ${fw.current.wind} mph, pressure ${fw.current.pressure} hPa, humidity ${fw.current.humidity}%`,
      "5-Day Forecast:",
      ...fw.forecast.map((d, i) => {
        const label = i === 0 ? "Today" : new Date(d.date + "T12:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
        const precip = parseFloat(d.precip) > 0 ? `, ${d.precip}″ precip` : "";
        return `  ${label}: ${d.desc}, H ${d.high}°F / L ${d.low}°F, wind ${d.wind} mph${precip}, sunrise ${d.sunrise} / sunset ${d.sunset}`;
      }),
    ].join("\n") : null;

    const enrichedMessage = `[User zip: ${zip} | Tier: ${tier}]
    ${liveCtx ? `\nLIVE CONDITIONS CONTEXT:\n${liveCtx}\n` : ""}
    ${forecastBlock ? `\nWEATHER FORECAST:\n${forecastBlock}\n` : ""}
    Response style: ${depthNote}

    User's question: ${input.trim()}`;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        setMessages(prev => [...prev, { role: "assistant", content: "Session expired — sign in again." }]);
        setLoading(false);
        return;
      }
      const res  = await fetch(EDGE_FN_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${session.access_token}` },
        body: JSON.stringify({ message: enrichedMessage, messages: history, zip: zip, isBriefing: false }),
      });
      const json = await res.json();
      if (json.error === "LIMIT_REACHED") {
        setMessages(prev => [...prev, { role: "assistant", content: null, limitHit: true }]);
      } else {
        setMessages(prev => [...prev, { role: "assistant", content: json.reply }]);
        const { data } = await supabase.from("profiles")
          .select("tier, queries_today, queries_reset_at, zip_code, email")
          .eq("id", user.id).single();
        setProfile(data);
      }
    } catch {
      setMessages(prev => [...prev, { role: "assistant", content: "Something went wrong — give it another shot." }]);
    } finally {
      setLoading(false);
    }
  }

  function handleKey(e) {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  }

  const tier      = profile?.tier ?? "free";
  const limit     = TIER_LIMITS[tier] ?? TIER_LIMITS.free;
  const used      = profile?.queries_today ?? 0;
  const remaining = Math.max(0, limit - used);
  const isAtLimit = remaining === 0;

  function formatBriefing(text) {
    return text.split("\n").filter(l => l.trim()).map((line, i) => {
      const parts = line.split(/\*\*(.*?)\*\*/g);
      return (
        <p key={i} className="fw-briefing-para">
          {parts.map((p, j) => j % 2 === 1 ? <strong key={j}>{p}</strong> : p)}
        </p>
      );
    });
  }

  function ConditionsBadge() {
    if (!spot || condLoading) return condLoading
      ? <span className="fw-cond-badge fw-cond-loading">Checking conditions…</span>
      : null;
    const badges = [];
    if (usgsData?.cfs != null) {
      const [mn, mx] = spot.flowRange ?? [0, 9999];
      const status = usgsData.cfs > mx * 1.5 ? "high" : usgsData.cfs < mn * 0.5 ? "low" : "good";
      const label  = status === "good" ? "✓ Flow good" : status === "high" ? "↑ Flow high" : "↓ Flow low";
      badges.push(<span key="flow" className={`fw-cond-badge fw-cond-${status}`}>{label} · {usgsData.cfs} CFS</span>);
    }
    if (usgsData?.tempF != null) {
      const warn = usgsData.tempF > 68 ? " ⚠️" : "";
      badges.push(<span key="temp" className="fw-cond-badge fw-cond-temp">{usgsData.tempF}°F{warn}</span>);
    }
    if (weatherData?.[0]) {
      const w = weatherData[0];
      badges.push(<span key="wx" className="fw-cond-badge fw-cond-wx">{w.temp}°{w.tempUnit} · {w.short}</span>);
    }
    return badges.length ? <div className="fw-conditions-row">{badges}</div> : null;
  }

  // ─────────────────────────────────────────────
  // UNAUTHENTICATED
  // ─────────────────────────────────────────────
  if (!user) {
    return (
      <div className="fw-container fw-unauthenticated">
        <div className="fw-landing">
          <div className="fw-landing-logo">
            <img src={LOGO_MAIN} alt="Fish Whistler" className="fw-landing-logo-img" />
            <div className="fw-landing-wordmark">
              <h1 style={{ color: "#ea580c" }}>Fish Whistler</h1>
              <p>Your AI-powered fishing guide — always on, always local</p>
            </div>
          </div>
          <div className="fw-landing-card">
            <p className="fw-landing-card-headline">The guide who never sleeps. The local who knows every run.</p>
            <p className="fw-landing-card-body">
              Fish Whistler is an AI fishing guide built for anglers who want real answers — not generic advice.
              Every morning you get a personalized daily briefing: the best nearby spot, what to tie on, what you'll catch,
              and the exact window to be on the water. Then ask anything — hatches, regulations, gear, secret runs —
              and get the kind of answer you'd normally only get from a guide who just stepped off the water.
            </p>
            <div className="fw-features">
              <div className="fw-feature"><span className="fw-feature-icon">☀️</span><span><strong>Daily Briefing</strong> — personalized to your zip code, every morning. Always free.</span></div>
              <div className="fw-feature"><span className="fw-feature-icon">🎣</span><span><strong>AI Guide Chat</strong> — ask about hatches, flows, gear, regulations, and local spots.</span></div>
              <div className="fw-feature"><span className="fw-feature-icon">🗺️</span><span><strong>Thousands of spots</strong> — detailed intel on rivers, streams, and lakes nationwide.</span></div>
              <div className="fw-feature"><span className="fw-feature-icon">🌊</span><span><strong>Live conditions</strong> — real-time stream flows, water temps, and weather baked into every answer.</span></div>
            </div>
            <div className="fw-tier-preview">
              <span className="fw-tier-pill fw-tier-pill-free">Free — 3 questions/day</span>
              <span className="fw-tier-pill fw-tier-pill-angler">Angler — 20 questions/day</span>
              <span className="fw-tier-pill fw-tier-pill-guide">Guide — Unlimited</span>
            </div>
          </div>
          <div className="fw-landing-cta">
            <button className="fw-cta-primary" onClick={() => nav("Account")}>Sign in to get your briefing →</button>
            <button className="fw-cta-secondary" onClick={() => nav("Pricing")}>See plans & pricing</button>
          </div>
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────────
  // AUTHENTICATED APP
  // ─────────────────────────────────────────────
  return (
    <div className="fw-container">

      {/* Header */}
      <div className="fw-header">
        <div className="fw-header-left">
          <img src={LOGO_MINI} alt="Fish Whistler" className="fw-logo-img" />
          <div>
            <h1 className="fw-title">Talk to the Fish Whistler</h1>
            <span className="fw-subtitle">
              {spot ? `${spot.name} · ${spot.state}` : "Whistlin' so good, you'll be talking to fish in your sleep."}
            </span>
          </div>
        </div>
        <div className="fw-header-right">
          <div className="fw-header-actions">
            <span className={`fw-tier-badge fw-tier-${tier}`}>{tier.toUpperCase()}</span>
            {tier !== "guide" && (
              <button className="fw-header-upgrade-btn" onClick={() => nav("Pricing")}>Upgrade ↑</button>
            )}
          </div>
          <span className="fw-queries-left">
            {tier === "guide" ? "Unlimited questions" : `${remaining} / ${limit} questions left today`}
          </span>
        </div>
      </div>

      {/* Live conditions strip */}
      <ConditionsBadge />

      {/* Chat window */}
      <div className="fw-chat-window">

        {/* Daily Briefing Card */}
        <div className="fw-briefing-card">
          <div className="fw-briefing-header">
            <span className="fw-briefing-icon">☀️</span>
            <span className="fw-briefing-title">
              {spot ? `Today's Conditions & Briefing` : "Today's Fishing Briefing"}
            </span>
            <span className="fw-briefing-free-badge">ALWAYS FREE</span>
          </div>
          {(briefingLoading || condLoading) && (
            <div className="fw-briefing-loading">
              <span className="fw-typing-dot" /><span className="fw-typing-dot" /><span className="fw-typing-dot" />
              <span style={{ marginLeft: 10 }}>
                {condLoading ? "Pulling live conditions…" : "Scouting conditions near you…"}
              </span>
            </div>
          )}
          {briefing && !briefingLoading && !condLoading && (
            <div className="fw-briefing-body">{formatBriefing(briefing)}</div>
          )}
        </div>

        {/* Chat prompt */}
        {messages.length === 0 && !loading && (
          <div className="fw-chat-prompt">
            <p>Have a follow-up? Ask anything below 👇</p>
          </div>
        )}

        {/* Messages */}
        {messages.map((msg, i) => {
          if (msg.limitHit) return <LimitReachedCard key={i} tier={tier} nav={nav} />;
          return (
            <div key={i} className={`fw-message fw-message-${msg.role}`} ref={msg.role === "assistant" && i === messages.length - 1 ? lastReplyRef : null}>
              {msg.role === "assistant" && (
                <span className="fw-avatar" style={{ width: 64, height: 64 }}>
                  <img src={LOGO_MINI} alt="" className="fw-avatar-logo" style={{ width: 64, height: 64, objectFit: "contain" }} />
                </span>
              )}
              <div className="fw-bubble">{msg.content}</div>
              {msg.role === "user" && <span className="fw-avatar fw-avatar-user">👤</span>}
            </div>
          );
        })}

        {loading && (
          <div className="fw-message fw-message-assistant">
            <span className="fw-avatar"><img src={LOGO_MINI} alt="" className="fw-avatar-logo" /></span>
            <div className="fw-bubble fw-bubble-typing">
              <span className="fw-typing-dot" /><span className="fw-typing-dot" /><span className="fw-typing-dot" />
            </div>
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {/* Input area */}
      <div className="fw-input-area">
        {isAtLimit ? (
          <LimitReachedBanner tier={tier} nav={nav} />
        ) : (
          <>
            <textarea
              className="fw-input"
              rows={2}
              placeholder={spot
                ? `Ask about ${spot.name} — hatches, gear, access, regulations…`
                : "Ask about hatches, conditions, gear, regulations…"}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKey}
              disabled={loading}
            />
            <button className="fw-send-btn" onClick={sendMessage} disabled={loading || !input.trim()}>
              {loading ? "…" : "Send"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// LIMIT UI
// ─────────────────────────────────────────────
function LimitReachedCard({ tier, nav }) {
  return (
    <div className="fw-limit-card">
      <p className="fw-limit-title">
        {tier === "free" ? "That's your 3 free questions for today." : "Daily limit reached."}
      </p>
      <p className="fw-limit-sub">Upgrade to keep the conversation going — the fish aren't waiting.</p>
      {tier === "free"   && <button className="fw-upgrade-btn" onClick={() => nav("Pricing")}>Upgrade to Angler — 20 questions/day →</button>}
      {tier === "angler" && <button className="fw-upgrade-btn" onClick={() => nav("Pricing")}>Upgrade to Guide — Unlimited →</button>}
    </div>
  );
}

function LimitReachedBanner({ tier, nav }) {
  return (
    <div className="fw-limit-banner">
      <span>{tier === "free" ? "You've used your 3 free daily questions." : "Daily question limit reached."}</span>
      <button className="fw-upgrade-inline" onClick={() => nav("Pricing")}>
        {tier === "free" ? "Upgrade to Angler →" : "Upgrade to Guide →"}
      </button>
    </div>
  );
}